import {
	getBackendErrorMessage,
	isStandardBackendError,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import type { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';

/**
 * One toast per failed transaction mutation.
 *
 * Attach it with `useMutation({ onError })` and NEVER with `mutate(vars, { onError })`. A
 * mutation-level handler REPLACES the QueryClient default that ApiLayer registers (the one that
 * toasts `error_codes.<code>`), whereas a per-call callback is ADDITIVE — which is why a single
 * rejected edit used to raise two toasts, worded differently, for the same failure. The
 * mutation-level handler also fires independently of whether the calling component is still
 * mounted, and survives a second `.mutate()` on the same hook; per-call callbacks are dropped in
 * both cases, which is how a 400 could show the user nothing at all.
 *
 * Precedence matches ApiLayer's, and it has to: replacing the default means this is the ONLY thing
 * that speaks, so preferring the server's sentence unconditionally is what put untranslated
 * service-layer English in front of users on every transactions surface. `error_codes.<code>`
 * first, the server's own sentence when a code has no entry (better a raw sentence than nothing),
 * then the generic fallback.
 */
export const transactionMutationError = (
	error: unknown,
	tDefault: TranslateFunction<I18Namespaces.DEFAULT>,
	fallbackMessage: string
): void => {
	showToastV2({
		state: 'error',
		message: resolveTransactionErrorMessage(error, tDefault, fallbackMessage),
	});
};

const resolveTransactionErrorMessage = (
	error: unknown,
	tDefault: TranslateFunction<I18Namespaces.DEFAULT>,
	fallbackMessage: string
): string => {
	const serverMessage = getBackendErrorMessage(error);
	if (!isStandardBackendError(error)) {
		return serverMessage ?? fallbackMessage;
	}
	const key = `error_codes.${error.error?.code}`;
	const phrase = tDefault(key);
	// i18next echoes the key back on a miss, so an unmapped code must fall through rather than
	// render "error_codes.120239" at the user.
	if (phrase && !phrase.endsWith(key)) {
		return phrase;
	}
	return serverMessage ?? fallbackMessage;
};
